/* ==========================================================================
   ALPINE SIEGE - CORE SCRIPT
   --------------------------------------------------------------------------
   Deliberately small. Alpine Siege prefers Liquid and CSS to JavaScript, so
   this file only does what CSS genuinely cannot:

     1. Marks the document as script-capable, so progressive enhancement
        never leaves content hidden.
     2. Reveals elements as they enter the viewport (IntersectionObserver).
     3. Tracks header scroll state as a class, so styling stays in CSS.

   RULES FOR THIS FILE
     - No dependencies. No framework. No polyfills for evergreen browsers.
     - Never hide content that JavaScript is then responsible for showing,
       unless this script has already proven it is running.
     - Never re-implement Shopify-native behaviour (cart, variants, forms).
       Dawn already owns that and must keep owning it.
     - Every feature must re-initialise on `shopify:section:load` so the
       theme editor stays live.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;

  /* Signals to CSS that scripting is available. `.as-js .as-reveal` is what
     applies the hidden starting state, so a failed script load simply means
     everything renders visible - which is the correct fallback. */
  root.classList.add('as-js');

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Reveal on scroll
     Adds `is-revealed` once, then stops observing. Under reduced-motion, or
     without IntersectionObserver, everything is revealed immediately.
     ---------------------------------------------------------------------- */

  var revealObserver = null;

  function revealNow(element) {
    element.classList.add('is-revealed');
  }

  function getRevealObserver() {
    if (revealObserver) return revealObserver;

    revealObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          revealNow(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        // Trigger slightly before the element reaches the fold so the
        // transition finishes as it settles into view rather than after.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0
      }
    );

    return revealObserver;
  }

  function initReveal(scope) {
    var targets = (scope || document).querySelectorAll('.as-reveal:not(.is-revealed)');
    if (!targets.length) return;

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, revealNow);
      return;
    }

    var observer = getRevealObserver();
    Array.prototype.forEach.call(targets, function (target) {
      observer.observe(target);
    });
  }

  /* If the user turns reduced-motion on mid-session, stop animating and
     show anything still waiting. */
  if (typeof prefersReducedMotion.addEventListener === 'function') {
    prefersReducedMotion.addEventListener('change', function (event) {
      if (!event.matches) return;
      Array.prototype.forEach.call(document.querySelectorAll('.as-reveal'), revealNow);
    });
  }

  /* ------------------------------------------------------------------------
     Header scroll state
     Toggles `is-scrolled` on any `[data-as-header]` element. All visual
     consequences of that state belong in CSS, not here.
     ---------------------------------------------------------------------- */

  var SCROLL_THRESHOLD = 8;
  var scrollFrame = null;

  function applyScrollState() {
    scrollFrame = null;

    var headers = document.querySelectorAll('[data-as-header]');
    if (!headers.length) return;

    var scrolled = window.scrollY > SCROLL_THRESHOLD;
    Array.prototype.forEach.call(headers, function (header) {
      header.classList.toggle('is-scrolled', scrolled);
    });
  }

  function onScroll() {
    if (scrollFrame !== null) return;
    scrollFrame = window.requestAnimationFrame(applyScrollState);
  }

  function initHeaderState() {
    applyScrollState();
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  /* ------------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function init(scope) {
    initReveal(scope);
    initHeaderState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme editor: sections are injected and replaced without a page load,
     so re-run initialisation against the section that changed. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* Minimal public surface, for section-level scripts that need to
     re-initialise after replacing their own markup. */
  window.AlpineSiege = window.AlpineSiege || {};
  window.AlpineSiege.init = init;
})();
